import { Check, X, Minus, Loader2, Clock } from 'lucide-react';

export const TERMINAL = new Set(['success', 'failed', 'timeout', 'cancelled', 'expired']);

export function StatusDot({ status }) {
  const cls = {
    success: 'bg-green-400',
    running: 'bg-purple-400 animate-pulse',
    failed: 'bg-red-400',
    cancelled: 'bg-orange-400',
    timeout: 'bg-orange-400',
    expired: 'bg-orange-400',
    queued: 'bg-yellow-400 animate-pulse',
    idle: 'bg-slate-300',
  };
  return <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${cls[status] || cls.idle}`} />;
}

export function StatusLabel({ status }) {
  const map = {
    success: { cls: 'text-green-400', label: 'Success' },
    running: { cls: 'text-purple-400', label: 'Running' },
    failed: { cls: 'text-red-400', label: 'Failed' },
    cancelled: { cls: 'text-orange-500', label: 'Cancelled' },
    timeout: { cls: 'text-orange-500', label: 'Timed out' },
    expired: { cls: 'text-orange-500', label: 'Expired' },
    queued: { cls: 'text-yellow-400', label: 'Queued' },
    idle: { cls: 'text-slate-400', label: 'Idle' },
  };
  const s = map[status] || map.idle;
  return <span className={`text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

// A solid colored circle with a small glyph inside — checklist-style status
// indicator (bigger and more legible at a glance than the plain StatusDot).
// `size` is the circle's diameter in px; the glyph scales to match.
export function StatusIcon({ status, size = 14 }) {
  const map = {
    success: { bg: 'bg-green-600', icon: Check },
    failed: { bg: 'bg-red-500', icon: X },
    cancelled: { bg: 'bg-orange-500', icon: Minus },
    timeout: { bg: 'bg-orange-500', icon: Minus },
    expired: { bg: 'bg-orange-500', icon: Minus },
    running: { bg: 'bg-purple-500', icon: Loader2, spin: true },
    queued: { bg: 'bg-yellow-400', icon: Clock },
    idle: { bg: 'bg-slate-300', icon: null },
  };
  const s = map[status] || map.idle;
  const Icon = s.icon;
  return (
    <div
      className={`rounded-full flex items-center justify-center shrink-0 ${s.bg}`}
      style={{ width: size, height: size }}
    >
      {Icon && <Icon size={Math.round(size * 0.65)} strokeWidth={3} className={`text-white ${s.spin ? 'animate-spin' : ''}`} />}
    </div>
  );
}

function formatSeconds(secs) {
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${String(secs % 60).padStart(2, '0')}s`;
}

// Elapsed time between startedAt and endedAt (defaults to now, for a build
// still in progress) — "12s" under a minute, "1m 05s" otherwise.
export function formatDuration(startedAt, endedAt) {
  if (!startedAt) return null;
  const end = endedAt ? new Date(endedAt) : new Date();
  const secs = Math.max(0, Math.round((end - new Date(startedAt)) / 1000));
  return formatSeconds(secs);
}

// Sum of each repo's own build duration across a run's wavePlan — time spent
// actually building, not wall-clock time (which also includes the gaps
// between waves while the next trigger call is in flight).
export function formatTotalBuildDuration(wavePlan) {
  let totalSecs = 0;
  let any = false;
  wavePlan.forEach(wave => wave.repos.forEach(r => {
    if (!r.startedAt) return;
    any = true;
    const end = r.finishedAt ? new Date(r.finishedAt) : new Date();
    totalSecs += Math.max(0, Math.round((end - new Date(r.startedAt)) / 1000));
  }));
  return any ? formatSeconds(totalSecs) : null;
}
